import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Layout from "components/Layout";
import api from "utils/api";
import { buildRegistrationPayload } from "utils/registration";
import "styles/legal.css";

const REQUEST_TYPES = [
  { value: "wakeup-onsite", label: "Wake-Up vor Ort" },
  { value: "wakeup-plus", label: "Wake-Up+ Unternehmen" },
  { value: "large-group", label: "Größere Gruppe" },
  { value: "event", label: "Event" },
  { value: "outside-area", label: "Massageadresse außerhalb Hamburgs" },
  { value: "business-license", label: "Business / Starterpaket / Lizenzpartnerschaft" },
  { value: "other", label: "Sonstiges" },
];

const reasonToType = (reason, offer) => {
  if (offer === "wakeup-onsite") return "wakeup-onsite";
  if (offer === "wakeup-plus") return "wakeup-plus";
  if (reason === "groessere-gruppe") return "large-group";
  if (reason === "more-than-10") return "large-group";
  if (reason === "outside-area") return "outside-area";
  if (reason === "service-unavailable") return "wakeup-plus";
  if (reason === "sonderwunsch") return "other";
  return "other";
};

const KontaktPage = () => {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tracking = useMemo(() => buildRegistrationPayload(location), [location]);
  const initialType = reasonToType(params.get("reason"), params.get("offer"));

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    requestedOfferType: initialType,
    personCount: params.get("personCount") || "",
    address: params.get("address") || "",
    desiredDate: "",
    desiredTime: "",
    message: "",
    acceptedContact: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const updateForm = (key) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submitToBackend = async (payload) => {
    try {
      return await api.post("/inquiries", payload);
    } catch (err) {
      const status = err?.response?.status;
      if (status && status !== 404 && status !== 405) throw err;
      return api.post("/special-requests", payload);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setInfo(null);
    if (!form.acceptedContact) {
      setError("Bitte bestätige den Kontakt- und Datenschutzhinweis.");
      return;
    }

    const payload = {
      brand: "dr-hoffmann",
      requestedOfferType: form.requestedOfferType,
      personCount: form.personCount ? Number(form.personCount) : undefined,
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      company: form.company.trim(),
      address: form.address.trim(),
      desiredDate: form.desiredDate,
      desiredTime: form.desiredTime,
      message: form.message.trim(),
      reason: params.get("reason") || form.requestedOfferType,
      ...tracking,
    };

    if (process.env.NODE_ENV === "development") {
      console.debug("Inquiry payload fields", {
        fields: Object.keys(payload),
      });
    }

    setSubmitting(true);
    try {
      const result = await submitToBackend(payload);
      if (
        result?.ok ||
        result?.success ||
        result?.status === 200 ||
        result?.status === 201
      ) {
        setInfo(
          "Danke, deine Anfrage wurde übermittelt. Wir melden uns schnellstmöglich."
        );
        setForm((current) => ({
          ...current,
          name: "",
          email: "",
          phone: "",
          company: "",
          personCount: "",
          address: "",
          desiredDate: "",
          desiredTime: "",
          message: "",
          acceptedContact: false,
        }));
      } else {
        throw new Error(
          result?.message || "Anfrage konnte nicht verarbeitet werden."
        );
      }
    } catch (err) {
      const errorMsg = err?.message || String(err);
      if (/offline|network|econnrefused/i.test(errorMsg)) {
        setError(
          "Die Anfrage konnte gerade nicht übermittelt werden. Bitte versuche es gleich erneut."
        );
      } else {
        setError(
          errorMsg ||
            "Anfrage konnte nicht verarbeitet werden. Bitte versuche es gleich erneut."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="legal-page">
        <div className="legal-shell contact-shell">
          <span className="legal-kicker">Sonderwünsche &amp; Anfragen</span>
          <h1>Kontakt</h1>

          <p className="legal-meta">
            Für größere Gruppen, Events oder individuelle Dienstleistungs-Wünsche. Wir prüfen deine Anfrage persönlich
            und melden uns schnellstmöglich.
          </p>

          {error && <div className="auth-alert is-error">{error}</div>}
          {info && <div className="auth-alert is-success">{info}</div>}

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="contact-form__row">
              <div>
                <label htmlFor="contact-name">Name *</label>
                <input
                  id="contact-name"
                  required
                  value={form.name}
                  onChange={updateForm("name")}
                  autoComplete="name"
                />
              </div>
              <div>
                <label htmlFor="contact-email">E-Mail *</label>
                <input
                  id="contact-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={updateForm("email")}
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="contact-form__row">
              <div>
                <label htmlFor="contact-phone">Telefon</label>
                <input
                  id="contact-phone"
                  type="tel"
                  value={form.phone}
                  onChange={updateForm("phone")}
                  autoComplete="tel"
                />
              </div>
              <div>
                <label htmlFor="contact-company">Firma</label>
                <input
                  id="contact-company"
                  value={form.company}
                  onChange={updateForm("company")}
                  autoComplete="organization"
                />
              </div>
            </div>

            <div className="contact-form__row">
              <div>
                <label htmlFor="contact-type">Art der Anfrage *</label>
                <select
                  id="contact-type"
                  required
                  value={form.requestedOfferType}
                  onChange={updateForm("requestedOfferType")}
                >
                  {REQUEST_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="contact-person-count">Anzahl Personen</label>
                <input
                  id="contact-person-count"
                  type="number"
                  min="1"
                  value={form.personCount}
                  onChange={updateForm("personCount")}
                />
              </div>
            </div>

            <div>
              <label htmlFor="contact-address">Massageadresse / Einsatzort</label>
              <input
                id="contact-address"
                value={form.address}
                onChange={updateForm("address")}
                placeholder="Straße, Hausnummer, PLZ, Ort"
                autoComplete="street-address"
              />
            </div>

            <div className="contact-form__row">
              <div>
                <label htmlFor="contact-date">Wunschdatum</label>
                <input
                  id="contact-date"
                  type="date"
                  value={form.desiredDate}
                  onChange={updateForm("desiredDate")}
                />
              </div>
              <div>
                <label htmlFor="contact-time">Wunschzeit</label>
                <input
                  id="contact-time"
                  type="time"
                  value={form.desiredTime}
                  onChange={updateForm("desiredTime")}
                />
              </div>
            </div>

            <div>
              <label htmlFor="contact-message">Nachricht / Sonderwunsch</label>
              <textarea
                id="contact-message"
                rows={5}
                value={form.message}
                onChange={updateForm("message")}
                placeholder="Was dürfen wir für dich vorbereiten?"
              />
            </div>

            <label className="contact-checkbox">
              <input
                type="checkbox"
                checked={form.acceptedContact}
                onChange={updateForm("acceptedContact")}
              />
              <span>
                Ich bin damit einverstanden, dass das Dr. Hoffmann Team meine Angaben zur
                Bearbeitung der Anfrage verarbeitet und mich dazu kontaktiert.
              </span>
            </label>

            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? "Anfrage wird gesendet..." : "Anfrage senden"}
            </button>
          </form>

          <div className="contact-direct">
            <h2>Direkter Kontakt</h2>
            <p>
              <b>Telefon:</b>{" "} 
              <a href="tel:+491797336154">+49 179 7336154</a>
              <br/>
              <b>E-Mail:</b>{" "}
              <a href="mailto:info@dr-hoffmann-massage.de">info@dr-hoffmann-massage.de</a>
              <br />
              <b>Standort:</b> Cremon 11, 20457 Hamburg
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default KontaktPage;
